import React, { useEffect, useState, Fragment } from 'react';
import { Document } from '../../Models/Document';
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Stack,
  Button
} from '@mui/material';
import { useNavigate } from "react-router-dom";
import { ArrowRight } from '@mui/icons-material';
import DocumentTable from './DocumentTable';
import DocumentGrid from './DocumentGrid';
import './DocumentListingStyles.css';
import { useAPIContext } from '../../Context/APIContext';
import { DocumentListingPageType } from '../../Models/DocumentListingPageType';
import { Category } from '../../Models/Category';

interface DocumentListingProps {
  type: DocumentListingPageType;
  categoryIds: number[];
}

interface DocumentCategories {
  category: Category;
  documents: Document[];
}

export default function DocumentListing({type, categoryIds}: DocumentListingProps) {
  const [documentCategories, setDocumentCategories] = useState<DocumentCategories[]>([]);
  const [savedDocuments, setSavedDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchTerm] = useState<string>("");
  const { apiFetch } = useAPIContext();
  const navigate = useNavigate();
  const pageTitle = type === DocumentListingPageType.Staff ? "Staff Zone" : type === DocumentListingPageType.Students ? "Student Zone" : "";

  useEffect(() => {
    const fetchDocumentData = async () => {
      setLoading(true);

      var dc: DocumentCategories[] = [];
      for (const categoryId of categoryIds) {
        const category = await apiFetch<Category>(`Categories/${categoryId}`, 'GET', Category.fromJson);
        var documents = await apiFetch<Document[]>(`Documents?categoryId=${categoryId}&limit=4`, 'GET', Document.fromJsonArray);

        dc = [...dc, { category: category!, documents: documents || [] }];
      }
      setDocumentCategories(dc);

      var savedDocuments = await apiFetch<Document[]>('Users/SavedDocuments', 'GET', Document.fromJsonArray)
      setSavedDocuments(savedDocuments || []);

      setLoading(false);
    };
    fetchDocumentData();
  }, [apiFetch, categoryIds, type]);

  useEffect(() => {
    const searchDocumentData = async () => {
      setSearchLoading(true);
      const filteredData = await apiFetch<Document[]>(`Documents?limit=${1000}&searchText=${searchTerm.toLowerCase()}`, 'GET', Document.fromJsonArray);
      if (filteredData) setFilteredDocuments(filteredData);
      setSearchLoading(false);
    };
    searchDocumentData();
  }, [searchTerm, apiFetch]);

  const ViewAll = (categoryId: number) => navigate(`/guides/${categoryId}`);

  const renderCategory = (
    documents: Document[],
    category: Category
  ) => (
    <Box className="header-section" mt={2} sx={{ mb: {xs: 3, md: 2}}}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs>
          <Typography variant="h6" className="header-text">{category.name}</Typography>
        </Grid>
        <Grid item>
          <Button variant="text" color='primary' onClick={() => ViewAll(category.id)} endIcon={<ArrowRight />}>
            <b>View All</b>
          </Button>
        </Grid>
      </Grid>
      <DocumentTable Documents={documents} savedDocuments={savedDocuments}/>
    </Box>
  );

  return (
    <Stack
      spacing={2}
      sx={{
        alignItems: 'center',
        mx: { xs: 3, sm: 3, md: 3 },
        pb: 5,
        mt: { xs: 1, md: 0 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '1700px', pt: 1 }}>
        {/* Header */}
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          mb={2}
          wrap="wrap"
        >
          <Grid item sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              className="header-text"
              sx={{
                fontSize: '1.5em',
              }}
            >{pageTitle}</Typography>
          </Grid>
        </Grid>


        {/* Conditional Content */}
        {searchTerm === "" ? (
          loading ? (
            <Box textAlign="center">
              <CircularProgress style={{ color: '#3D9BE9' }} />
            </Box>
          ) : (
            <>
              {documentCategories.map(({ category, documents }) => (
                    <Fragment key={category.id}>
                      {renderCategory(documents, category)}
                    </Fragment>
                  ))}
            </>
          )
        ) : (
          searchLoading ? (
            <Box textAlign="center">
              <CircularProgress style={{ color: '#3D9BE9' }} />
            </Box>
          ) : filteredDocuments.length > 0 ? (
            <Box className="header-section">
              <DocumentGrid Documents={filteredDocuments} savedDocuments={savedDocuments} />
            </Box>
          ) : (
            <Typography variant="h6" align="center" color="textSecondary" sx={{ minHeight: '300px' }}>
              No Guides found.
            </Typography>
          )
        )}
      </Box>
    </Stack>
  );
}
